import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Link,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import authService from '../../services/auth';

const ISSUE_OPTIONS = [
  { value: 'forgot_password', label: 'Redefinição de senha não recebida' },
  { value: 'email_verification', label: 'Problema na verificação do e-mail' },
  { value: 'trial_link', label: 'Link do trial expirado ou inválido' },
  { value: 'login_credentials', label: 'Credenciais de acesso não funcionam' },
  { value: 'account_access', label: 'Acesso à conta ou problema técnico' },
];

export default function AccessSupportPanel({
  sourcePage = 'login',
  defaultEmail = '',
  title = 'Precisa de ajuda para acessar sua conta?',
  description = 'Envie um e-mail ao suporte Zulbot ou abra um ticket curto sobre login e credenciais.',
}) {
  const [form, setForm] = useState({
    name: '',
    email: defaultEmail,
    company_name: '',
    issue_type: 'login_credentials',
    description: '',
  });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await authService.createAccessSupportRequest({
        ...form,
        source_page: sourcePage,
      });
      setSuccess(
        response?.ticket_id
          ? `Ticket de suporte #${response.ticket_id} criado com sucesso.`
          : 'Solicitação de suporte criada com sucesso.'
      );
      setForm((current) => ({
        ...current,
        description: '',
      }));
    } catch (requestError) {
      setError(
          requestError?.response?.data?.error ||
          requestError?.response?.data?.detail ||
          'Não foi possível criar a solicitação de suporte.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {description}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Prefere e-mail?{' '}
        <Link href="mailto:support@zulbot.ai" underline="hover">
          support@zulbot.ai
        </Link>
      </Typography>

      <Button
        variant="text"
        sx={{ mt: 1.5, px: 0, textTransform: 'none' }}
        onClick={() => setOpen((current) => !current)}
      >
        {open ? 'Ocultar formulário de suporte' : 'Abrir ticket de suporte'}
      </Button>

      {open ? (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, display: 'grid', gap: 2 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {success ? <Alert severity="success">{success}</Alert> : null}

          <TextField
            label="Nome"
            value={form.name}
            onChange={(event) => handleChange('name', event.target.value)}
            required
          />
          <TextField
            label="E-mail"
            type="email"
            value={form.email}
            onChange={(event) => handleChange('email', event.target.value)}
            required
          />
          <TextField
            label="Empresa"
            value={form.company_name}
            onChange={(event) => handleChange('company_name', event.target.value)}
          />
          <TextField
            select
            label="Tipo de problema"
            value={form.issue_type}
            onChange={(event) => handleChange('issue_type', event.target.value)}
            required
          >
            {ISSUE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Descreva o problema de acesso"
            value={form.description}
            onChange={(event) => handleChange('description', event.target.value)}
            required
            multiline
            minRows={4}
            helperText="Não inclua senhas, códigos de verificação ou dados de cartão."
          />
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Enviando...' : 'Criar ticket de suporte'}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}
